/**
 * The countdown behind `AutoCloseNotice`, kept free of React and the DOM so it can be checked on its own.
 *
 * After a successful submission the result screen stays up for `AUTO_CLOSE_SECONDS`, counting down, and
 * then the tab closes itself:
 *  - counting    → "This tab will close in N seconds", with Close now / Stay on this page.
 *  - closing     → "Closing this tab…": `window.close()` has been asked for (at 0, or on Close now). Shown
 *                  for at most `CLOSE_CHECK_DELAY_MS`, until the tab goes or the browser is seen to have refused.
 *  - stayed      → the learner chose to stay; nothing closes.
 *  - notClosable → the browser kept the tab open (it only lets a script close a tab opened by a link or
 *                  a script with a single history entry), so the learner is told to close it themselves.
 */

/** How long the result screen stays up before the tab closes itself. */
export const AUTO_CLOSE_SECONDS = 7;

/** How long after `window.close()` a tab that is still open counts as one the browser would not close. */
export const CLOSE_CHECK_DELAY_MS = 500;

export type AutoClosePhase = "counting" | "closing" | "stayed" | "notClosable";

export interface AutoCloseState {
  phase: AutoClosePhase;
  secondsLeft: number;
}

export type AutoCloseAction = { type: "tick" } | { type: "closeNow" } | { type: "stay" } | { type: "closeRefused" };

export const initialAutoCloseState: AutoCloseState = { phase: "counting", secondsLeft: AUTO_CLOSE_SECONDS };

/**
 * Every action is ignored outside the phase it belongs to, so a late tick, a second click on Close now
 * or a Stay after closing has begun can never ask the browser to close the tab twice or undo a choice.
 */
export const autoCloseReducer = (state: AutoCloseState, action: AutoCloseAction): AutoCloseState => {
  switch (action.type) {
    case "tick":
      if (state.phase !== "counting") return state;
      return state.secondsLeft <= 1 ? { phase: "closing", secondsLeft: 0 } : { ...state, secondsLeft: state.secondsLeft - 1 };
    case "closeNow":
      return state.phase === "counting" ? { phase: "closing", secondsLeft: 0 } : state;
    case "stay":
      return state.phase === "counting" ? { ...state, phase: "stayed" } : state;
    case "closeRefused":
      return state.phase === "closing" ? { ...state, phase: "notClosable" } : state;
    default:
      return state;
  }
};

/** The note shown under the result screen for a state. */
export const autoCloseMessage = ({ phase, secondsLeft }: AutoCloseState): string => {
  if (phase === "stayed") return "Your response is saved. You can close this tab whenever you like.";
  if (phase === "notClosable") return "Your response is saved. You can close this tab now.";
  if (phase === "closing") return "Closing this tab…";
  return `This tab will close in ${secondsLeft} ${secondsLeft === 1 ? "second" : "seconds"}`;
};

/**
 * What screen readers hear for a state: the start of the countdown once, then only the outcome. The
 * seconds in between are shown but not announced, so the live region does not read out every one of them.
 */
export const autoCloseAnnouncement = (state: AutoCloseState): string =>
  state.phase === "counting" ? autoCloseMessage(initialAutoCloseState) : autoCloseMessage(state);

/**
 * True when the page is running inside a frame. A framed page cannot close the tab it sits in — the host
 * page owns that, and already hears `formSubmitted` — so the countdown is not offered there at all.
 */
export const isInFrame = (win: Window): boolean => {
  try {
    return win.self !== win.top;
  } catch {
    // Reading `top` across origins can throw in older browsers; only a framed page gets that far.
    return true;
  }
};
